import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";
import { ArrowLeftRight, CircleCheck, CircleSlash, CircleX, Clock, Pause, Play, Power, RefreshCw, RotateCcw, TriangleAlert } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import type { DatasetRow, DatasetStatus, SyncResponse } from "../../../shared/sync";
import { useConfirm } from "../../components/Confirm";
import { ReconnectButton } from "../../components/ConnectionBanner";
import { ErrorNotice, MenuItem, MoreMenu, Panel, ProgressBar, Spinner, StableLabel } from "../../components/ui";
import { api, inFrame } from "../../lib/api";
import { useCompany } from "../../lib/company";
import { count, dateTime, every, monthYear, timeAgo, timeUntil } from "../../lib/format";
import { useSyncStatus } from "../../lib/queries";

/*
 * Settings › ServiceM8: the connection, then everything the app keeps from ServiceM8: anything
 * that needs someone to act (with the fix beside it), then each kind of data with its controls
 * (the title bar's are for all of it) and, under any still loading its history, a progress bar.
 * Live: the server sends sync.changed as work starts, progresses and finishes. Everyone else sees
 * the sync's state in the header (SyncStatus). Last, deleting everything kept for the business
 * (the privacy policy's promise).
 */

type Action = (path: string, body?: unknown) => void;
type AllAction = (action: "now" | "pause" | "resume") => void;

/** Statuses where a first sync or re-read is under way (or waiting its turn). */
const LOADING = new Set<DatasetStatus>(["running", "queued", "waiting", "paused"]);
/**
 * ServiceM8 last said no (not switched on, or a first sync that failed before reading anything):
 * a check now only asks again, so it isn't shown as a first sync until a page actually arrives.
 */
const rechecking = (row: DatasetRow) => row.unavailable !== null || (row.full.state === "needed" && row.lastError !== null);
const isLoading = (row: DatasetRow) => row.full.state !== "done" && LOADING.has(row.status) && !rechecking(row);
/** A check is already running or about to (not a history load, which can take days: Check now still works then). */
const isChecking = (row: DatasetRow) => (row.status === "running" || row.status === "queued") && !isLoading(row);
const needsAttention = (row: DatasetRow) => row.status === "error" || row.status === "no-permission";

export function ServiceM8Section() {
    const company = useCompany();
    const { data, error, isLoading: loading } = useSyncStatus(company.id);
    const base = `/c/${company.id}/sync`;
    const all = useMutation({ mutationFn: (action: "now" | "pause" | "resume") => api(`${base}/all/${action}`, { method: "POST" }) });

    const attention = data?.connected ? data.datasets.filter(needsAttention) : [];

    return (
        <>
            <Connection />
            {loading ? <Spinner /> : null}
            <div className="mb-6 empty:hidden">
                <ErrorNotice error={error ?? all.error} />
            </div>
            {data ? (
                <>
                    {attention.length > 0 && <Attention rows={attention} base={base} />}
                    <Panel
                        title="Synced from ServiceM8"
                        actions={<AllActions paused={data.paused} datasets={data.datasets} busy={all.isPending} run={(action) => all.mutate(action)} />}
                        flush
                    >
                        <DatasetTable datasets={data.datasets} base={base} />
                        <LiveLegend />
                    </Panel>
                    <Usage usage={data.usage} />
                </>
            ) : null}
            <DeleteData />
        </>
    );
}

function Connection() {
    const company = useCompany();
    const connected = company.connection?.status === "active";
    const confirm = useConfirm();
    const disconnect = useMutation({ mutationFn: () => api(`/c/${company.id}/connection`, { method: "DELETE" }) });
    const askDisconnect = async () => {
        const yes = await confirm({
            title: "Disconnect ServiceM8?",
            message: "Syncing stops and changes can't be saved to ServiceM8 until it's connected again. Nothing in ServiceM8 is deleted.",
            confirmLabel: "Disconnect",
            tone: "danger",
        });
        if (yes) disconnect.mutate();
    };

    return (
        <Panel title="ServiceM8 connection">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-2">
                    {connected ? <CircleCheck className="size-5 text-brand-500" /> : <CircleX className="size-5 text-danger" />}
                    <div>
                        <div className="font-semibold text-ink">{connected ? "Connected" : "Not connected"}</div>
                        <div className="text-muted">
                            {connected ? (
                                <>
                                    Data is synced both ways: below is what's synced and when.
                                </>
                            ) : (
                                (company.connection?.error ?? "Connect to sync with ServiceM8.")
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex gap-2">
                    {connected && (
                        <button type="button" className="btn-secondary" disabled={disconnect.isPending} onClick={() => void askDisconnect()}>
                            Disconnect
                        </button>
                    )}
                    {/* ServiceM8's sign-in page won't load inside ServiceM8, so from there it opens a tab. */}
                    <a
                        href={inFrame ? "/servicem8/connect" : "/servicem8/connect?from=web"}
                        target={inFrame ? "_blank" : undefined}
                        rel="noreferrer"
                        className="btn-primary"
                    >
                        {connected ? "Reconnect" : "Connect"}
                    </a>
                </div>
            </div>
            <ErrorNotice error={disconnect.error} />
        </Panel>
    );
}

/**
 * The panel title bar's controls, for all the data: Pause all / Resume all, and Check all (the main
 * action, in green).
 */
function AllActions({ paused, datasets, busy, run }: Readonly<{ paused: boolean; datasets: DatasetRow[]; busy: boolean; run: AllAction }>) {
    const pressed = usePressedCheck();
    const checking = !paused && (pressed.showing || datasets.some(isChecking));

    return (
        <div className="flex items-center justify-end gap-1.5">
            <button
                type="button"
                className={clsx(paused ? "btn-positive" : "btn-secondary", "px-3 py-1.5")}
                disabled={busy}
                title={paused ? "Carry on updating everything, catching up on anything missed." : "Stop updating everything for now. Changes made meanwhile are picked up when resumed."}
                onClick={() => run(paused ? "resume" : "pause")}
            >
                {paused ? <Play className="size-4" /> : <Pause className="size-4" />}
                <StableLabel labels={["Pause all", "Resume all"]} shown={paused ? "Resume all" : "Pause all"} />
            </button>
            <button
                type="button"
                className="btn-primary px-3 py-1.5"
                disabled={busy || paused || checking}
                title="Fetch anything changed in ServiceM8 since the last check, for all the data. This normally happens by itself."
                onClick={() => {
                    pressed.start();
                    run("now");
                }}
            >
                <RefreshCw className={clsx("size-4", checking && "animate-spin [animation-duration:2s]")} />
                <StableLabel labels={["Check all", "Checking…"]} shown={checking ? "Checking…" : "Check all"} />
            </button>
        </div>
    );
}

/**
 * "Checking…" for a moment after a Check button is pressed. A quick check can start and finish
 * between two status updates, so the page would never see it running; this shows it anyway.
 * Longer checks carry on showing through their status (isChecking).
 */
function usePressedCheck(): { showing: boolean; start: () => void } {
    const [showing, setShowing] = useState(false);
    useEffect(() => {
        if (!showing) return;
        const timer = setTimeout(() => setShowing(false), 1000);
        return () => clearTimeout(timer);
    }, [showing]);
    return { showing, start: () => setShowing(true) };
}

/** A POST to one dataset's control (now, pause, resume, resync, enabled). */
function useDatasetAction(base: string, id: string) {
    return useMutation({ mutationFn: (a: { path: string; body?: unknown }) => api(`${base}/${id}/${a.path}`, { method: "POST", body: a.body }) });
}

// ---------------------------------------------------------------------------------------------
// Needs attention: each problem with its fix beside it
// ---------------------------------------------------------------------------------------------

/**
 * Deleting everything the app keeps for this business, for good (server: companyData.ts). Typing
 * the business's name confirms it (the server checks it too): there's no undo. Only the account
 * holder or whoever connected ServiceM8 can (company.canDeleteData).
 */
function DeleteData() {
    const company = useCompany();
    const [open, setOpen] = useState(false);
    const [typed, setTyped] = useState("");
    const remove = useMutation({
        mutationFn: () => api(`/c/${company.id}/data`, { method: "DELETE", body: { confirm: typed } }),
        // Its pages are gone: start again (another business, or none).
        onSuccess: () => globalThis.location.assign("/"),
    });
    const close = () => {
        setOpen(false);
        setTyped("");
    };
    return (
        <Panel title="Delete this business's data">
            <p className="m-0 text-muted">
                Removes everything this app keeps for <strong className="text-ink">{company.name}</strong>: its copy of your ServiceM8 data,
                settings, price list work, and the sign-ins of people who only use it for this business. Nothing in ServiceM8 changes. It
                can&apos;t be undone, and it&apos;s cleared from backups within 30 days. To stop using the add-on altogether, remove it in
                ServiceM8 too.{" "}
                <a href="/privacy" target="_blank" rel="noreferrer" className="text-brand-ink">
                    Privacy policy
                </a>
            </p>
            {!company.canDeleteData && (
                <p className="m-0 mt-4 text-muted">Only the ServiceM8 account holder, or whoever connected ServiceM8 here, can delete it.</p>
            )}
            {company.canDeleteData && open ? (
                <div className="mt-4 flex flex-wrap items-end gap-2">
                    <label className="m-0 block min-w-64 flex-1 space-y-1.5">
                        <span className="text-sm">
                            Type <strong>{company.name}</strong> to confirm
                        </span>
                        <input className="input" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
                    </label>
                    <button type="button" className="btn-secondary" onClick={close} disabled={remove.isPending}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn-danger"
                        disabled={typed.trim() !== company.name.trim() || remove.isPending}
                        onClick={() => remove.mutate()}
                    >
                        <StableLabel labels={["Delete everything", "Deleting..."]} shown={remove.isPending ? "Deleting..." : "Delete everything"} />
                    </button>
                </div>
            ) : null}
            {company.canDeleteData && !open ? (
                <button type="button" className="btn-secondary mt-4" onClick={() => setOpen(true)}>
                    Delete all data...
                </button>
            ) : null}
            <ErrorNotice error={remove.error} />
        </Panel>
    );
}

/** Problems with the data (while connected: not connected is the connection panel's to say). */
function Attention({ rows, base }: Readonly<{ rows: DatasetRow[]; base: string }>) {
    return (
        <Panel title="Needs attention" flush>
            <ul>
                {rows.map((row) => (
                    <AttentionItem key={row.id} row={row} base={base} />
                ))}
            </ul>
        </Panel>
    );
}

function AttentionItem({ row, base }: Readonly<{ row: DatasetRow; base: string }>) {
    const action = useDatasetAction(base, row.id);
    const noPermission = row.status === "no-permission";
    const text = noPermission
        ? `The ServiceM8 connection doesn't let the app read ${row.name.toLowerCase()}. Reconnecting asks ServiceM8 for permission.`
        : `${(row.lastError ?? "Something went wrong").replace(/\.$/, "")}. It tries again by itself ${timeUntil(row.retryAt) || "soon"}.`;

    return (
        <li className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line px-5 py-3 first:border-t-0">
            <TriangleAlert className={clsx("size-4 shrink-0", noPermission ? "text-warning" : "text-danger")} />
            <div className="min-w-0 flex-1">
                <div className="font-semibold">{row.name}</div>
                <div className="text-muted" title={noPermission ? undefined : dateTime(row.lastErrorAt)}>
                    {text}
                </div>
                {action.error ? <div className="text-danger">{action.error.message}</div> : null}
            </div>
            {noPermission ? <ReconnectButton /> : (
                <button type="button" className="btn-secondary px-3 py-1" disabled={action.isPending} onClick={() => action.mutate({ path: "now" })}>
                    <RefreshCw className="size-3.5" /> Try now
                </button>
            )}
        </li>
    );
}

// ---------------------------------------------------------------------------------------------
// Your data: one row per kind of data, with its controls (and, while it loads its history, a
// progress row underneath)
// ---------------------------------------------------------------------------------------------

/** Statuses where nothing is being updated: turned off, paused, not allowed, or not switched on in ServiceM8. */
const STOPPED = new Set<DatasetStatus>(["off", "paused", "no-permission", "unavailable"]);

/**
 * How the data is kept up to date, before its name: the two-arrow icon (as on synced items
 * elsewhere) for data ServiceM8 sends changes for as they happen (webhooks), in green; a grey
 * clock for data checked on a timer (hover: how often). Either is faint while nothing is updated
 * (turned off, paused, not allowed or not switched on): never red, as that's not a problem.
 */
function LiveIcon({ row }: Readonly<{ row: DatasetRow }>) {
    const stopped = STOPPED.has(row.status);
    let label = row.webhooks ? "Live: updated as soon as something changes in ServiceM8" : `Updated ${every(row.everyMs)}`;
    if (stopped) label = "Not being updated at the moment";
    return (
        <span role="img" title={label} aria-label={label} className="inline-flex">
            {row.webhooks ? (
                <ArrowLeftRight className={clsx("size-3.5", stopped ? "text-muted/40" : "text-brand-500")} />
            ) : (
                <Clock className={clsx("size-3.5", stopped ? "text-muted/40" : "text-muted")} />
            )}
        </span>
    );
}

function LiveLegend() {
    return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line px-5 py-3 text-xs text-muted">
            <span className="inline-flex items-center gap-1.5">
                <ArrowLeftRight className="size-3.5 text-brand-500" /> Live: updated as soon as something changes
            </span>
            <span className="inline-flex items-center gap-1.5">
                <Clock className="size-3.5" /> Updated regularly
            </span>
        </div>
    );
}

/**
 * Fixed columns and rows of one height: every cell is a main line and one smaller line (cut short
 * with "…" rather than wrapping; the full text shows on hover), so nothing moves as it updates.
 */
function DatasetTable({ datasets, base }: Readonly<{ datasets: DatasetRow[]; base: string }>) {
    return (
        <table className="list is-fixed min-w-[900px]">
            <colgroup>
                <col className="w-[29%]" />
                <col className="w-[11%]" />
                <col className="w-[40%]" />
                <col className="w-[20%]" />
            </colgroup>
            <thead>
                <tr>
                    <th>Data</th>
                    <th className="text-right">Records</th>
                    <th>Status</th>
                    <th>
                        <span className="sr-only">Actions</span>
                    </th>
                </tr>
            </thead>
            <tbody>
                {datasets.map((row) => (
                    <DatasetLine key={row.id} row={row} base={base} />
                ))}
            </tbody>
        </table>
    );
}

/** A cell's two lines: the main one, and a smaller one underneath (always there, even if empty, so rows match). */
function Lines({ main, sub, subClass = "text-muted", align }: Readonly<{ main: ReactNode; sub?: ReactNode; subClass?: string; align?: "right" }>) {
    return (
        <div className={clsx("min-w-0", align === "right" && "text-right")}>
            <div className="truncate">{main}</div>
            <div className={clsx("h-4 truncate text-xs leading-4", subClass)}>{sub}</div>
        </div>
    );
}

function DatasetLine({ row, base }: Readonly<{ row: DatasetRow; base: string }>) {
    const action = useDatasetAction(base, row.id);
    const status = rowStatus(row);

    return (
        <>
            <tr>
                <td className="h-16">
                    <Lines
                        main={
                            <span className="inline-flex items-center gap-1.5">
                                <LiveIcon row={row} />
                                <span className="font-semibold">{row.name}</span>
                            </span>
                        }
                        sub={<span title={row.description}>{row.description}</span>}
                    />
                </td>
                <td>
                    {/* Just the number (centred, as the row's height is fixed) unless some aren't active. */}
                    {row.records === row.activeRecords ? (
                        <div className="text-right">{count(row.records)}</div>
                    ) : (
                        <Lines align="right" main={count(row.records)} sub={`${count(row.activeRecords)} active`} />
                    )}
                </td>
                <td>
                    <Lines
                        main={
                            <span className="inline-flex items-center gap-1.5" title={status.title}>
                                {status.icon}
                                {status.label}
                            </span>
                        }
                        sub={action.error ? <span title={action.error.message}>{action.error.message}</span> : status.sub}
                        subClass={action.error ? "text-danger" : undefined}
                    />
                </td>
                <td>
                    <Actions row={row} busy={action.isPending} run={(path, body) => action.mutate({ path, body })} />
                </td>
            </tr>
            {isLoading(row) ? <ProgressLine row={row} columns={4} /> : null}
        </>
    );
}

const HISTORY_STATE: Partial<Record<DatasetStatus, string>> = {
    running: "Loading now",
    queued: "Waiting its turn",
    waiting: "Carries on tomorrow",
    paused: "Paused",
};

/** Under a row loading its history (first sync or re-read): a bar and how far it's got, newest first. */
function ProgressLine({ row, columns }: Readonly<{ row: DatasetRow; columns: number }>) {
    const { full } = row;
    const what = full.completedAt === null ? "First sync" : "Re-reading everything";
    // A re-read roughly knows how many records to expect (what was here); a first sync doesn't.
    const value = full.expected > 0 ? full.fetched / full.expected : null;
    let progress = full.state === "running" ? `${count(full.fetched)} read so far, newest first` : "Not started yet";
    if (full.state === "running" && full.reachedBack) progress += `, back to ${monthYear(full.reachedBack)}`;
    if (value !== null) progress += ` (about ${Math.round(Math.min(1, value) * 100)}%)`;

    return (
        <tr>
            <td colSpan={columns} className="border-t-0 pt-0 pb-4">
                <div className="flex items-center gap-3 text-xs text-muted">
                    <span className="shrink-0 font-medium text-ink">{what}</span>
                    <ProgressBar className="flex-1" value={row.status === "running" || value !== null ? value : 0} label={`${row.name}: ${what}`} />
                    <span className="w-72 shrink-0 truncate" title={progress}>
                        {progress}
                    </span>
                    <span className="w-32 shrink-0 text-right">{HISTORY_STATE[row.status]}</span>
                </div>
            </td>
        </tr>
    );
}

interface RowStatus {
    icon: ReactNode;
    label: string;
    sub?: ReactNode;
    title?: string;
}

/** A row's status in plain words: what's happening, and when it last (or next) updates. */
function rowStatus(row: DatasetRow): RowStatus {
    return heldBack(row) ?? asking(row) ?? (isLoading(row) ? loadingStatus(row) : activeStatus(row));
}

/** Asking ServiceM8 again about data it said isn't switched on (Check again, or the 6-hourly check). */
function asking(row: DatasetRow): RowStatus | null {
    if (row.unavailable === null || (row.status !== "running" && row.status !== "queued")) return null;
    return {
        icon: <RefreshCw className={clsx("size-4 text-info", row.status === "running" && "animate-spin [animation-duration:2s]")} />,
        label: "Asking ServiceM8…",
        title: row.unavailable,
        sub: "Seeing whether it's switched on now.",
    };
}

/** Rows that aren't updating: turned off, paused, or something in the way. */
function heldBack(row: DatasetRow): RowStatus | null {
    switch (row.status) {
        case "off":
            return { icon: <Power className="size-4 text-warning" />, label: "Turned off", sub: "Not updated. What's here is kept." };
        case "paused":
            if (isLoading(row)) return null;
            return {
                icon: <Pause className="size-4 text-warning" />,
                label: "Paused",
                sub: (
                    <span title={dateTime(row.lastSyncAt)}>
                        {row.lastSyncAt ? `Last updated ${timeAgo(row.lastSyncAt)}. ` : ""}Catches up when resumed.
                    </span>
                ),
            };
        case "no-permission":
            return { icon: <TriangleAlert className="size-4 text-warning" />, label: "Needs reconnecting", sub: "See Needs attention above." };
        case "error":
            return { icon: <TriangleAlert className="size-4 text-danger" />, label: "Problem", sub: "See Needs attention above." };
        case "unavailable":
            return {
                icon: <CircleSlash className="size-4 text-muted" />,
                label: "Not switched on in ServiceM8",
                title: row.unavailable ?? undefined,
                // lastErrorAt: when ServiceM8 last said it isn't switched on.
                sub: (
                    <span title={`Last checked ${dateTime(row.lastErrorAt)}. Next check ${dateTime(row.retryAt)}.`}>
                        {row.lastErrorAt ? `Checked ${timeAgo(row.lastErrorAt)} · ` : ""}next {timeUntil(row.retryAt) || "soon"}
                    </span>
                ),
            };
        default:
            return null;
    }
}

function loadingStatus(row: DatasetRow): RowStatus {
    const spinning = row.status === "running";
    return {
        icon: <RefreshCw className={clsx("size-4 text-info", spinning && "animate-spin [animation-duration:2s]")} />,
        label: row.status === "paused" ? "Paused while loading history" : "Loading history",
        sub: "Recent records are ready to use.",
    };
}

function activeStatus(row: DatasetRow): RowStatus {
    const checked = <span title={dateTime(row.lastSyncAt)}>{row.lastSyncAt ? `Checked ${timeAgo(row.lastSyncAt)}` : "Not checked yet"}</span>;
    if (row.status === "running" || row.status === "queued") {
        return {
            icon: <RefreshCw className={clsx("size-4 text-info", row.status === "running" && "animate-spin [animation-duration:2s]")} />,
            label: row.status === "running" ? "Checking for changes…" : "Checking shortly",
            sub: checked,
        };
    }
    return {
        icon: <CircleCheck className="size-4 text-brand-ink" />,
        label: "Up to date",
        sub: (
            <span>
                {checked}
                {row.nextRunAt ? <span title={dateTime(row.nextRunAt)}> · next {timeUntil(row.nextRunAt)}</span> : null}
            </span>
        ),
    };
}

/** The row's one button: what it most needs (Turn on, Resume, Check again), otherwise Check now. */
function mainAction(row: DatasetRow): { label: string; path: string; body?: unknown; icon: ReactNode; help: string } {
    if (row.status === "off") {
        return { label: "Turn on", path: "enabled", body: { enabled: true }, icon: <Power className="size-3.5" />, help: "Start syncing it again, catching up from where it stopped." };
    }
    if (row.status === "paused") {
        return { label: "Resume", path: "resume", icon: <Play className="size-3.5" />, help: "Carry on updating it, catching up on anything missed." };
    }
    if (row.status === "unavailable") {
        return { label: "Check again", path: "now", icon: <RefreshCw className="size-3.5" />, help: "Ask ServiceM8 again now whether it's switched on." };
    }
    return {
        label: "Check now",
        path: "now",
        icon: <RefreshCw className="size-3.5" />,
        help: "Fetch anything changed in ServiceM8 since the last check. This normally happens by itself.",
    };
}

/**
 * One button for what the row most needs (usually Check now), and a ⋯ menu for the rest, each
 * saying what it does. Re-reading everything asks first, in the menu.
 */
function Actions({ row, busy, run }: Readonly<{ row: DatasetRow; busy: boolean; run: Action }>) {
    const pressed = usePressedCheck();
    const main = mainAction(row);
    // Turned off or paused: the button (Turn on, Resume) is in green, to prompt starting it again.
    const stopped = row.status === "off" || row.status === "paused";
    const checking = isChecking(row) || (pressed.showing && main.path === "now");
    const click = () => {
        if (main.path === "now") pressed.start();
        run(main.path, main.body);
    };
    return (
        <div className="flex items-center justify-end gap-1.5">
            <button
                type="button"
                className={clsx(stopped ? "btn-positive" : "btn-secondary", "h-8 px-2.5 py-0 text-xs")}
                disabled={busy || checking || row.status === "no-permission"}
                title={checking ? "Checking with ServiceM8 now." : main.help}
                onClick={click}
            >
                {checking ? <RefreshCw className="size-3.5 animate-spin [animation-duration:2s]" /> : main.icon}
                <StableLabel labels={["Check now", "Check again", "Resume", "Turn on", "Checking…"]} shown={checking ? "Checking…" : main.label} />
            </button>
            <MoreMenu label={`More for ${row.name}`}>{(close) => <ActionMenu row={row} busy={busy} run={run} close={close} />}</MoreMenu>
        </div>
    );
}

function ActionMenu({ row, busy, run, close }: Readonly<{ row: DatasetRow; busy: boolean; run: Action; close: () => void }>) {
    const confirm = useConfirm();
    const off = row.status === "off";
    const paused = row.status === "paused";
    // Nothing to pause or re-read while ServiceM8 doesn't allow it or hasn't switched it on.
    const usable = row.status !== "no-permission" && row.status !== "unavailable";
    const what = row.name.toLowerCase();
    const runAndClose: Action = (path, body) => {
        run(path, body);
        close();
    };

    // Reading it all again can take a while: it asks first (useConfirm).
    const reread = async () => {
        close();
        const yes = await confirm({
            title: `Re-read all ${what} from ServiceM8?`,
            message: `What's here stays until it's replaced${row.kind === "windowed" ? ". It's read newest first and can take a while." : "."}`,
            confirmLabel: "Re-read everything",
        });
        if (yes) run("resync");
    };

    return (
        <>
            {!off && (
                <MenuItem
                    icon={paused ? <Play className="size-4" /> : <Pause className="size-4" />}
                    label={paused ? "Resume" : "Pause"}
                    help={paused ? "Carry on updating it, catching up on anything missed." : "Stop updating it for now. Changes made meanwhile are picked up when resumed."}
                    disabled={busy || !usable}
                    onClick={() => runAndClose(paused ? "resume" : "pause")}
                />
            )}
            {!off && (
                <MenuItem
                    icon={<RotateCcw className="size-4" />}
                    label="Re-read everything"
                    help={`Read every one of the ${what} again from the start. Only needed if something looks wrong.`}
                    disabled={busy || !usable}
                    onClick={() => void reread()}
                />
            )}
            {row.optional && (
                <MenuItem
                    icon={<Power className="size-4" />}
                    label={off ? "Turn on" : "Turn off"}
                    help={off ? `Start syncing ${what} again, catching up from where it stopped.` : `Stop syncing ${what} if you don't need them here. What's here is kept.`}
                    danger={!off}
                    disabled={busy}
                    onClick={() => runAndClose("enabled", { enabled: off })}
                />
            )}
        </>
    );
}

// ---------------------------------------------------------------------------------------------
// ServiceM8 requests today
// ---------------------------------------------------------------------------------------------

function Usage({ usage }: Readonly<{ usage: SyncResponse["usage"] }>) {
    return (
        <Panel title="ServiceM8 requests today">
            <div className="grid gap-4 sm:grid-cols-2">
                <UsageBar label="All requests" used={usage.requests} limit={usage.dailyLimit} />
                <UsageBar label="Loading history" used={usage.background} limit={usage.backgroundLimit} />
            </div>
            <p className="mt-3 mb-0 text-xs text-muted">
                ServiceM8 allows {count(usage.dailyLimit)} requests a day. Loading history stops at its share and carries on the next
                day (UTC), so there's always room for everyday updates and saving changes.
            </p>
        </Panel>
    );
}

function UsageBar({ label, used, limit }: Readonly<{ label: string; used: number; limit: number }>) {
    return (
        <div className="space-y-1">
            <div className="flex justify-between">
                <span>{label}</span>
                <span className="text-muted">
                    {count(used)} of {count(limit)}
                </span>
            </div>
            <ProgressBar value={limit ? used / limit : 1} label={`${label} today`} />
        </div>
    );
}
